import { Link } from 'react-router-dom';
import { useBalance } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useUsageByKey, useUsageHourly, useUsageSummary } from '../api/useUsage.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { formatTimestamp, formatTokens, formatUsd } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Tenant dashboard — first surface after sign-in.
 *
 * Layout: four stat cards (balance, this-month cost, active keys
 * count, last request timestamp) + a recent-activity table that shows
 * the most recent 10 hourly buckets from `/v1/usage/hourly?days=7`.
 *
 * We deliberately read multiple endpoints in parallel — React Query
 * handles the concurrency + loading state individually. A stat card
 * stays on `—` until its own query resolves; one slow endpoint does
 * not block the rest of the dashboard.
 *
 * The "last request" value is derived from the most-recent non-empty
 * hourly bucket. Platform-api does not yet surface a direct "last
 * request" timestamp, and adding one would duplicate usage accounting
 * logic; the derived value is good enough for a glance-pace read.
 */

function firstOfMonthIso(): string {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
  return start.toISOString();
}

function nextMonthStartIso(): string {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0, 0));
  return start.toISOString();
}

export function Dashboard(): JSX.Element {
  const t = useT();
  const balanceQ = useBalance();
  const usageQ = useUsageSummary({ from: firstOfMonthIso(), to: nextMonthStartIso() });
  const keysQ = useKeysList();
  const hourlyQ = useUsageHourly(7);
  const byKeyQ = useUsageByKey({ from: firstOfMonthIso(), to: nextMonthStartIso() });

  const activeKeys = keysQ.data?.keys.filter((k) => k.revokedAt === null).length ?? null;

  const lastBucket =
    (hourlyQ.data?.buckets ?? [])
      .filter((b) => b.requests > 0)
      .sort((a, b) => b.hour.localeCompare(a.hour))[0] ?? null;
  const lastRequestAt = lastBucket?.hour ?? null;

  const cards: readonly {
    labelKey: I18nKey;
    value: string;
    hint: JSX.Element;
    testId: string;
  }[] = [
    {
      labelKey: 'dashboard.stat.balance.label',
      value: balanceQ.data !== undefined ? formatUsd(balanceQ.data.balanceUsdCents) : '—',
      hint:
        balanceQ.data !== undefined ? (
          balanceQ.data.balanceUsdCents > 0 ? (
            <span>
              {t('dashboard.stat.balance.updated', {
                timestamp: formatTimestamp(balanceQ.data.updatedAt),
              })}
            </span>
          ) : (
            <Link
              to="/billing/topup"
              className="font-medium text-primary hover:text-primary-fixed-dim"
            >
              {t('dashboard.stat.balance.zero_cta')}
            </Link>
          )
        ) : balanceQ.isError ? (
          <span>{t('dashboard.stat.balance.unavailable')}</span>
        ) : (
          <span>{t('dashboard.stat.balance.loading')}</span>
        ),
      testId: 'dashboard-stat-balance',
    },
    {
      labelKey: 'dashboard.stat.cost.label',
      value: usageQ.data !== undefined ? formatUsd(usageQ.data.totalCostUsdCents) : '—',
      hint:
        usageQ.data !== undefined ? (
          <span>
            {t('dashboard.stat.cost.tokens', {
              tokens: formatTokens(usageQ.data.totalInputTokens + usageQ.data.totalOutputTokens),
            })}
          </span>
        ) : usageQ.isError ? (
          <span>{t('dashboard.stat.cost.unavailable')}</span>
        ) : (
          <span>{t('dashboard.stat.cost.loading')}</span>
        ),
      testId: 'dashboard-stat-cost',
    },
    {
      labelKey: 'dashboard.stat.keys.label',
      value: activeKeys !== null ? String(activeKeys) : '—',
      hint: keysQ.isError ? (
        <span>{t('dashboard.stat.keys.unavailable')}</span>
      ) : activeKeys === 0 ? (
        <Link to="/keys" className="font-medium text-primary hover:text-primary-fixed-dim">
          {t('dashboard.stat.keys.empty_cta')}
        </Link>
      ) : (
        <span>{t('dashboard.stat.keys.description')}</span>
      ),
      testId: 'dashboard-stat-keys',
    },
    {
      labelKey: 'dashboard.stat.last_request.label',
      value:
        lastRequestAt !== null
          ? formatTimestamp(lastRequestAt)
          : t('dashboard.stat.last_request.empty'),
      hint:
        lastRequestAt !== null ? (
          <span>{t('dashboard.stat.last_request.hint')}</span>
        ) : (
          <Link to="/keys" className="font-medium text-primary hover:text-primary-fixed-dim">
            {t('dashboard.stat.last_request.empty_cta')}
          </Link>
        ),
      testId: 'dashboard-stat-last-request',
    },
  ];

  return (
    <section data-testid="dashboard-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('dashboard.title')}
        </h2>
        <p className="text-sm text-outline">{t('dashboard.subtitle')}</p>
      </header>

      <div
        data-testid="dashboard-stat-grid"
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
      >
        {cards.map((card) => (
          <article
            key={card.testId}
            data-testid={card.testId}
            className="flex flex-col gap-3 rounded-xl bg-surface-container p-6"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
              {t(card.labelKey)}
            </span>
            <span className="text-4xl font-semibold tracking-tight text-primary">{card.value}</span>
            <div className="text-xs text-outline">{card.hint}</div>
          </article>
        ))}
      </div>

      <section
        data-testid="dashboard-activity"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <header className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('dashboard.activity.title')}
          </h3>
          <Link
            to="/usage"
            data-testid="dashboard-activity-link"
            className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline transition-colors hover:text-on-surface"
          >
            {t('dashboard.activity.view_all')}
          </Link>
        </header>
        <DashboardActivity
          buckets={(hourlyQ.data?.buckets ?? []).slice(-10).reverse()}
          isLoading={hourlyQ.isLoading}
          isError={hourlyQ.isError}
        />
      </section>

      <section
        data-testid="dashboard-top-keys"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <header className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('dashboard.top_keys.title')}
          </h3>
          <Link
            to="/keys"
            data-testid="dashboard-top-keys-link"
            className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline transition-colors hover:text-on-surface"
          >
            {t('dashboard.top_keys.manage')}
          </Link>
        </header>
        <DashboardTopKeys
          entries={(byKeyQ.data?.keys ?? []).slice(0, 5)}
          isLoading={byKeyQ.isLoading}
          isError={byKeyQ.isError}
        />
      </section>
    </section>
  );
}

interface ActivityProps {
  readonly buckets: readonly {
    hour: string;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    costUsdCents: number;
    costUsd: string;
  }[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

function DashboardActivity({ buckets, isLoading, isError }: ActivityProps): JSX.Element {
  const t = useT();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="dashboard-activity-loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        <span className="sr-only">{t('dashboard.activity.loading')}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-error" role="alert">
        {t('dashboard.activity.error')}
      </p>
    );
  }
  if (buckets.length === 0) {
    return (
      <div className="flex flex-col gap-2 text-sm text-outline">
        <p>{t('dashboard.activity.empty')}</p>
        <Link to="/keys" className="font-medium text-primary hover:text-primary-fixed-dim">
          {t('dashboard.activity.empty_cta')}
        </Link>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="dashboard-activity-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
            <th className="px-3 py-2 font-semibold">{t('dashboard.activity.column.hour')}</th>
            <th className="px-3 py-2 font-semibold">{t('dashboard.activity.column.requests')}</th>
            <th className="px-3 py-2 font-semibold">{t('dashboard.activity.column.tokens')}</th>
            <th className="px-3 py-2 font-semibold">{t('dashboard.activity.column.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr
              key={b.hour}
              data-testid={`dashboard-activity-row-${b.hour}`}
              className="odd:bg-surface-container-low"
            >
              <td className="px-3 py-2 font-mono text-on-surface">{formatTimestamp(b.hour)}</td>
              <td className="px-3 py-2 font-mono text-on-surface">{formatTokens(b.requests)}</td>
              <td className="px-3 py-2 font-mono text-on-surface-variant">
                {formatTokens(b.inputTokens + b.outputTokens)}
              </td>
              <td className="px-3 py-2 font-mono text-primary">{formatUsd(b.costUsdCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TopKeysProps {
  readonly entries: readonly {
    apiKeyId: string;
    apiKeyName: string | null;
    apiKeyPrefix: string | null;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    costUsdCents: number;
    costUsd: string;
  }[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

function DashboardTopKeys({ entries, isLoading, isError }: TopKeysProps): JSX.Element {
  const t = useT();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="dashboard-top-keys-loading">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        <span className="sr-only">{t('dashboard.top_keys.loading')}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-error" role="alert">
        {t('dashboard.top_keys.error')}
      </p>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <StatusChip variant="neutral" label={t('dashboard.top_keys.empty.chip')} />
        <span className="text-sm text-outline">{t('dashboard.top_keys.empty.hint')}</span>
        <Link to="/keys" className="text-sm font-medium text-primary hover:text-primary-fixed-dim">
          {t('dashboard.top_keys.empty_cta')}
        </Link>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="dashboard-top-keys-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
            <th className="px-3 py-2 font-semibold">{t('dashboard.top_keys.column.key')}</th>
            <th className="px-3 py-2 font-semibold">{t('dashboard.top_keys.column.requests')}</th>
            <th className="px-3 py-2 font-semibold">{t('dashboard.top_keys.column.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((k) => (
            <tr
              key={k.apiKeyId}
              className="odd:bg-surface-container-low"
              data-testid={`dashboard-top-key-${k.apiKeyId}`}
            >
              <td className="px-3 py-2 font-semibold text-on-surface">
                {keyDisplayName(k.apiKeyName, k.apiKeyPrefix, t)}
              </td>
              <td className="px-3 py-2 font-mono text-on-surface">{formatTokens(k.requests)}</td>
              <td className="px-3 py-2 font-mono text-primary">{formatUsd(k.costUsdCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return t('usage.by_key.unnamed_key');
  return t('usage.by_key.unknown_key');
}
